import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { PageHeader, DataTable, EmptyState, StatCard } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, LifeBuoy, RotateCcw, Inbox, Clock, CheckCircle2, UserCheck } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import {
  StatusPill, SUPPORT_CATEGORY_LABELS, SUPPORT_PRIORITY_STYLES, SUPPORT_STATUS_LABELS,
} from "@/pages/seller/Support";

const PAGE_SIZE = 50;
const ALL = "all";
const STATUSES = ["OPEN", "IN_PROGRESS", "AWAITING_REPLY", "RESOLVED", "CLOSED"];

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const AdminSupport = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const [sellerId, setSellerId] = useLocalStorageState("admin-support:seller", ALL);
  const [status, setStatus] = useLocalStorageState("admin-support:status", ALL);
  const [assignee, setAssignee] = useLocalStorageState("admin-support:assignee", ALL);
  const [search, setSearch] = useLocalStorageState("admin-support:search", "");
  const [searchInput, setSearchInput] = useState(search);
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ sellers: [], assignees: [] });
  const abortRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/support/filter-options`)
      .then((r) => { if (!cancelled) setOptions({ sellers: r.data?.sellers || [], assignees: r.data?.assignees || [] }); })
      .catch(() => { if (!cancelled) setOptions({ sellers: [], assignees: [] }); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (sellerId && sellerId !== ALL) params.seller_id = sellerId;
      if (status && status !== ALL) params.status = status;
      if (assignee && assignee !== ALL) params.assignee_id = assignee;
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/admin/support`, { params, signal: controller.signal });
      setData({ items: res.items || [], total: res.total || 0, counts: res.counts || {} });
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      toast.error(errDetail(e, "Failed to load tickets"));
    } finally {
      setLoading(false);
    }
  }, [page, sellerId, status, assignee, search]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { setPage(1); }, [sellerId, status, assignee, search]);

  const resetFilters = () => { setSellerId(ALL); setStatus(ALL); setAssignee(ALL); setSearch(""); setSearchInput(""); };
  const hasFilters = (sellerId !== ALL) || (status !== ALL) || (assignee !== ALL) || !!search;
  const counts = data.counts || {};
  const openish = (counts.OPEN || 0) + (counts.IN_PROGRESS || 0) + (counts.AWAITING_REPLY || 0);

  const columns = [
    { key: "ticket", header: "Ticket", primary: true,
      cell: (r) => (
        <button onClick={() => navigate(`/admin/support/${r.id}`)} className="text-left">
          <span className="text-sm font-medium text-brand hover:underline">{r.ticket_number}</span>
          <div className="text-sm text-slate-800 line-clamp-1 max-w-[260px]">{r.subject}</div>
        </button>
      ) },
    { key: "seller", header: "Seller", cell: (r) => <span className="text-sm font-medium text-slate-800">{r.seller_brand_name || r.seller_id}</span> },
    { key: "category", header: "Category", hideOnMobile: true, cell: (r) => <span className="text-sm text-slate-600">{SUPPORT_CATEGORY_LABELS[r.category] || r.category}</span> },
    { key: "priority", header: "Priority", hideOnMobile: true, cell: (r) => <span className={`text-sm font-medium capitalize ${SUPPORT_PRIORITY_STYLES[r.priority] || ""}`}>{r.priority}</span> },
    { key: "status", header: "Status", cell: (r) => <StatusPill status={r.status} /> },
    { key: "assignee", header: "Assignee", hideOnMobile: true,
      cell: (r) => r.assignee ? <span className="text-sm text-slate-700">{r.assignee.name || r.assignee.email}</span> : <span className="text-sm text-slate-400">Unassigned</span> },
    { key: "activity", header: "Last activity", hideOnMobile: true,
      cell: (r) => <div className="text-xs text-slate-500"><div>{formatToIST(r.last_reply_at || r.updated_at)}</div>{r.last_reply_by && <div className="text-slate-400">{r.last_reply_by === "seller" ? "Seller replied" : "We replied"}</div>}</div> },
  ];

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader title="Support" subtitle="Manage and resolve seller support tickets across all sellers" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Open / Active" value={Number(openish).toLocaleString("en-IN")} icon={Inbox} accent="warning" loading={loading} trend="Open + In Progress + Awaiting" />
          <StatCard label="Awaiting Reply" value={Number(counts.AWAITING_REPLY || 0).toLocaleString("en-IN")} icon={Clock} accent="brand" loading={loading} />
          <StatCard label="Resolved" value={Number(counts.RESOLVED || 0).toLocaleString("en-IN")} icon={CheckCircle2} accent="success" loading={loading} />
          <StatCard label="All Tickets" value={Number(counts.all || 0).toLocaleString("en-IN")} icon={UserCheck} accent="info" loading={loading} />
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Seller</Label>
                <Select value={sellerId} onValueChange={setSellerId}>
                  <SelectTrigger><SelectValue placeholder="All sellers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All sellers</SelectItem>
                    {options.sellers.map((s) => <SelectItem key={s.seller_id} value={s.seller_id}>{s.brand_name || s.seller_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All statuses</SelectItem>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{SUPPORT_STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Assignee</Label>
                <Select value={assignee} onValueChange={setAssignee}>
                  <SelectTrigger><SelectValue placeholder="Anyone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Anyone</SelectItem>
                    {options.assignees.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input placeholder="Ticket #, subject, order # or AWB" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
                </div>
              </div>
            </div>
            {hasFilters && (
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={resetFilters}><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear filters</Button>
              </div>
            )}
          </CardContent>
        </Card>

        <DataTable
          columns={columns}
          data={data.items}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/admin/support/${r.id}`)}
          loading={loading}
          empty={<EmptyState icon={LifeBuoy} title="No tickets match these filters" description={hasFilters ? "Try widening the seller / status / assignee filters." : "No support tickets have been raised yet."} action={hasFilters ? <Button variant="outline" onClick={resetFilters}><RotateCcw className="w-4 h-4 mr-2" /> Clear filters</Button> : undefined} />}
          pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminSupport;
